/**
 * InferenceAccuracy — admin calibration metrics card.
 * Memory Plan v1, Phase 5a. Mounted at /admin?tab=digests.
 */
import React, { useEffect, useState, useCallback } from "react";
import { Loader2, AlertCircle, RefreshCw, Target } from "lucide-react";
import { authAxios } from "../../utils/auth";

function pctColor(p) {
  if (p == null) return "text-white/40";
  if (p >= 75) return "text-emerald-400";
  if (p >= 50) return "text-amber-400";
  return "text-rose-400";
}

export default function InferenceAccuracy() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [windowDays, setWindowDays] = useState(30);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authAxios.get(
        `/admin/analytics/inference-accuracy?days=${windowDays}`
      );
      setData(res.data);
    } catch (e) {
      setError(
        e?.response?.data?.detail || e?.message || "Failed to load accuracy"
      );
    } finally {
      setLoading(false);
    }
  }, [windowDays]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div
      data-testid="inference-accuracy"
      className="mt-8 border-t border-white/10 pt-6 space-y-4"
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Target className="w-4 h-4 text-divine-gold" />
          <div>
            <h3 className="text-divine-gold font-cinzel text-base">
              Inference accuracy
            </h3>
            <p className="text-white/40 text-xs">
              User-confirmed vs corrected predictions over last {windowDays}d.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid="accuracy-window"
            value={windowDays}
            onChange={(e) => setWindowDays(parseInt(e.target.value, 10))}
            className="bg-black/40 border border-white/10 rounded px-2 py-1 text-xs text-white"
          >
            <option value={7}>7d</option>
            <option value={30}>30d</option>
            <option value={90}>90d</option>
            <option value={180}>180d</option>
          </select>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="text-white/50 hover:text-divine-gold p-1 disabled:opacity-40"
            data-testid="accuracy-refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {error ? (
        <div className="flex items-start gap-2 p-3 text-rose-300 text-sm border border-rose-500/30 rounded">
          <AlertCircle className="w-4 h-4 mt-0.5" />
          <span>{error}</span>
        </div>
      ) : null}

      {loading && !data ? (
        <div className="flex items-center gap-2 text-white/60 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading accuracy…
        </div>
      ) : null}

      {data ? (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div
              data-testid="accuracy-card-total"
              className="border border-white/10 rounded-md p-3 bg-divine-black/40"
            >
              <div className="text-white/40 text-[10px] uppercase tracking-wide">
                Predictions labelled
              </div>
              <div className="text-divine-gold text-2xl font-semibold mt-1">
                {data.total_predictions}
              </div>
            </div>
            <div
              data-testid="accuracy-card-confirmed"
              className="border border-emerald-500/20 rounded-md p-3 bg-emerald-500/5"
            >
              <div className="text-emerald-300 text-[10px] uppercase tracking-wide">
                Confirmed
              </div>
              <div className="text-emerald-400 text-2xl font-semibold mt-1">
                {data.confirmed}
              </div>
              {data.by_source ? (
                <div
                  data-testid="accuracy-card-confirmed-split"
                  className="mt-1.5 flex items-center gap-2 text-[10px] text-emerald-300/80"
                >
                  <span title="Confirmed in chat via inline confirmation">
                    💬 {data.by_source.chat?.confirmed ?? 0}
                  </span>
                  <span className="text-emerald-500/40">·</span>
                  <span title="Confirmed in panel via resolve button">
                    ✓ {data.by_source.panel?.confirmed ?? 0}
                  </span>
                </div>
              ) : null}
            </div>
            <div
              data-testid="accuracy-card-corrected"
              className="border border-rose-500/20 rounded-md p-3 bg-rose-500/5"
            >
              <div className="text-rose-300 text-[10px] uppercase tracking-wide">
                Corrected
              </div>
              <div className="text-rose-400 text-2xl font-semibold mt-1">
                {data.corrected}
              </div>
              {data.by_source ? (
                <div
                  data-testid="accuracy-card-corrected-split"
                  className="mt-1.5 flex items-center gap-2 text-[10px] text-rose-300/80"
                >
                  <span title="Corrected in chat via inline confirmation">
                    💬 {data.by_source.chat?.corrected ?? 0}
                  </span>
                  <span className="text-rose-500/40">·</span>
                  <span title="Corrected in panel (retract / edit / confidence)">
                    ✓ {data.by_source.panel?.corrected ?? 0}
                  </span>
                </div>
              ) : null}
            </div>
            <div
              data-testid="accuracy-card-pct"
              className="border border-divine-gold/20 rounded-md p-3 bg-divine-black/40"
            >
              <div className="text-white/40 text-[10px] uppercase tracking-wide">
                Accuracy
              </div>
              <div
                className={`text-2xl font-semibold mt-1 ${pctColor(
                  data.accuracy_pct
                )}`}
              >
                {data.accuracy_pct != null ? `${data.accuracy_pct}%` : "—"}
              </div>
            </div>
          </div>

          {data.by_domain && Object.keys(data.by_domain).length > 0 ? (
            <div className="space-y-2">
              <h4 className="text-divine-gold/80 text-xs uppercase tracking-wide">
                By domain
              </h4>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {Object.entries(data.by_domain).map(([domain, b]) => (
                  <li
                    key={domain}
                    data-testid={`accuracy-domain-${domain}`}
                    className="border border-white/5 rounded p-2 bg-divine-black/30 flex items-center justify-between"
                  >
                    <span className="text-white/80 text-xs">{domain}</span>
                    <span className="flex items-center gap-2 text-xs">
                      <span className="text-emerald-400">{b.confirmed}</span>
                      <span className="text-white/30">/</span>
                      <span className="text-rose-400">{b.corrected}</span>
                      <span className={`font-medium ${pctColor(b.accuracy_pct)}`}>
                        {b.accuracy_pct != null ? `${b.accuracy_pct}%` : "—"}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
